import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { useToast } from '../../hooks/use-toast';
import api, { formatApiError } from '../../lib/api';
import { Plus, ShoppingCart, Loader2, Trash2, Package } from 'lucide-react';

const inr = (n) => `\u20B9${(n || 0).toLocaleString('en-IN')}`;
const emptyProduct = { name: '', price: '', stock: '', sku: '' };

const AccessoriesPage = () => {
  const [products, setProducts] = useState([]);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);

  const [prodOpen, setProdOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [prodForm, setProdForm] = useState(emptyProduct);
  const [savingProd, setSavingProd] = useState(false);

  const [saleOpen, setSaleOpen] = useState(false);
  const [cart, setCart] = useState([]);
  const [pick, setPick] = useState({ accessory_id: '', qty: '1' });
  const [customer, setCustomer] = useState('');
  const [savingSale, setSavingSale] = useState(false);

  const { toast } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, s] = await Promise.all([
        api.get('/accessories', { params: { active_only: true } }),
        api.get('/accessories/sales', { params: { limit: 50 } }),
      ]);
      setProducts(p.data);
      setSales(s.data);
    } catch (err) {
      toast({ title: 'Failed to load accessories', description: formatApiError(err), variant: 'destructive' });
    } finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  // ── Product CRUD ──
  const openNew = () => { setEditing(null); setProdForm(emptyProduct); setProdOpen(true); };
  const openEdit = (p) => { setEditing(p); setProdForm({ name: p.name, price: String(p.price), stock: String(p.stock), sku: p.sku || '' }); setProdOpen(true); };

  const saveProduct = async () => {
    if (!prodForm.name.trim() || prodForm.price === '') { toast({ title: 'Name and price are required', variant: 'destructive' }); return; }
    setSavingProd(true);
    try {
      const payload = { name: prodForm.name, price: Number(prodForm.price), stock: Number(prodForm.stock) || 0, sku: prodForm.sku || null };
      if (editing) await api.put(`/accessories/${editing.id}`, payload);
      else await api.post('/accessories', payload);
      toast({ title: editing ? 'Product updated' : 'Product added' });
      setProdOpen(false); load();
    } catch (err) { toast({ title: 'Failed', description: formatApiError(err), variant: 'destructive' }); }
    finally { setSavingProd(false); }
  };

  const removeProduct = async (id) => {
    try { await api.delete(`/accessories/${id}`); toast({ title: 'Product removed' }); load(); }
    catch (err) { toast({ title: 'Failed', description: formatApiError(err), variant: 'destructive' }); }
  };

  // ── Sales ──
  const openSale = () => { setCart([]); setPick({ accessory_id: '', qty: '1' }); setCustomer(''); setSaleOpen(true); };

  const addToCart = () => {
    const prod = products.find((p) => p.id === pick.accessory_id);
    if (!prod) { toast({ title: 'Select a product', variant: 'destructive' }); return; }
    const qty = Number(pick.qty) || 1;
    setCart((c) => {
      const existing = c.find((i) => i.accessory_id === prod.id);
      if (existing) return c.map((i) => i.accessory_id === prod.id ? { ...i, qty: i.qty + qty } : i);
      return [...c, { accessory_id: prod.id, name: prod.name, price: prod.price, qty }];
    });
    setPick({ accessory_id: '', qty: '1' });
  };

  const cartTotal = cart.reduce((s, i) => s + i.price * i.qty, 0);

  const completeSale = async () => {
    if (cart.length === 0) { toast({ title: 'Add at least one item', variant: 'destructive' }); return; }
    setSavingSale(true);
    try {
      const res = await api.post('/accessories/sale', {
        items: cart.map((i) => ({ accessory_id: i.accessory_id, qty: i.qty })),
        customer_name: customer || null,
      });
      toast({ title: 'Sale recorded! 🛍️', description: `Total ${inr(res.data.total)}` });
      setSaleOpen(false); load();
    } catch (err) { toast({ title: 'Sale failed', description: formatApiError(err), variant: 'destructive' }); }
    finally { setSavingSale(false); }
  };

  if (loading) return <div className="flex justify-center py-32"><Loader2 className="h-10 w-10 animate-spin text-blue-500" /></div>;

  return (
    <div data-testid="admin-accessories">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Accessories</h1>
          <p className="text-gray-500 text-sm mt-1">Manage retail products (goggles, caps) and record sales</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={openNew} data-testid="add-product-btn"><Plus className="mr-2 h-4 w-4" /> Add Product</Button>
          <Button onClick={openSale} className="bg-gradient-to-r from-amber-500 to-orange-500" data-testid="record-sale-btn"><ShoppingCart className="mr-2 h-4 w-4" /> Record Sale</Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Products */}
        <Card className="shadow-md border-0 bg-white">
          <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Package className="h-5 w-5" /> Products</CardTitle></CardHeader>
          <CardContent className="p-0">
            {products.length === 0 ? (
              <div className="text-center py-12 text-gray-500" data-testid="products-empty">No products yet. Add goggles, caps, etc.</div>
            ) : (
              <Table data-testid="products-table">
                <TableHeader>
                  <TableRow><TableHead>Name</TableHead><TableHead>Price</TableHead><TableHead>Stock</TableHead><TableHead className="text-right">Actions</TableHead></TableRow>
                </TableHeader>
                <TableBody>
                  {products.map((p) => (
                    <TableRow key={p.id} data-testid={`product-row-${p.id}`}>
                      <TableCell className="font-medium">{p.name}{p.sku && <span className="ml-2 text-xs text-gray-400 font-mono">{p.sku}</span>}</TableCell>
                      <TableCell>{inr(p.price)}</TableCell>
                      <TableCell><Badge className={p.stock > 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>{p.stock}</Badge></TableCell>
                      <TableCell className="text-right space-x-1">
                        <Button size="sm" variant="outline" onClick={() => openEdit(p)} data-testid={`edit-product-${p.id}`}>Edit</Button>
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" onClick={() => removeProduct(p.id)} data-testid={`remove-product-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Sales log */}
        <Card className="shadow-md border-0 bg-white">
          <CardHeader><CardTitle className="text-lg flex items-center gap-2"><ShoppingCart className="h-5 w-5" /> Recent Sales</CardTitle></CardHeader>
          <CardContent className="p-0">
            {sales.length === 0 ? (
              <div className="text-center py-12 text-gray-500" data-testid="sales-empty">No sales recorded yet.</div>
            ) : (
              <Table data-testid="sales-table">
                <TableHeader>
                  <TableRow><TableHead>Date</TableHead><TableHead>Items</TableHead><TableHead>Customer</TableHead><TableHead className="text-right">Total</TableHead></TableRow>
                </TableHeader>
                <TableBody>
                  {sales.map((s) => (
                    <TableRow key={s.id} data-testid={`sale-row-${s.id}`}>
                      <TableCell className="text-sm">{s.date}</TableCell>
                      <TableCell className="text-sm text-gray-600">{s.items.map((i) => `${i.name} ×${i.qty}`).join(', ')}</TableCell>
                      <TableCell className="text-sm">{s.customer_name || '—'}</TableCell>
                      <TableCell className="text-right font-semibold">{inr(s.total)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Product dialog */}
      <Dialog open={prodOpen} onOpenChange={setProdOpen}>
        <DialogContent data-testid="product-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Product' : 'Add Product'}</DialogTitle>
            <DialogDescription>Retail accessory item (e.g. goggles, swim cap).</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="space-y-2 col-span-2"><Label>Name</Label><Input value={prodForm.name} onChange={(e) => setProdForm((p) => ({ ...p, name: e.target.value }))} placeholder="e.g. Swim Goggles" data-testid="product-name-input" /></div>
            <div className="space-y-2"><Label>Price (₹)</Label><Input type="number" value={prodForm.price} onChange={(e) => setProdForm((p) => ({ ...p, price: e.target.value }))} data-testid="product-price-input" /></div>
            <div className="space-y-2"><Label>Stock</Label><Input type="number" value={prodForm.stock} onChange={(e) => setProdForm((p) => ({ ...p, stock: e.target.value }))} data-testid="product-stock-input" /></div>
            <div className="space-y-2 col-span-2"><Label>SKU (optional)</Label><Input value={prodForm.sku} onChange={(e) => setProdForm((p) => ({ ...p, sku: e.target.value }))} placeholder="GOG-01" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProdOpen(false)}>Cancel</Button>
            <Button onClick={saveProduct} disabled={savingProd} className="bg-gradient-to-r from-blue-600 to-cyan-600" data-testid="product-save-btn">
              {savingProd && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {editing ? 'Update' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sale dialog */}
      <Dialog open={saleOpen} onOpenChange={setSaleOpen}>
        <DialogContent data-testid="sale-dialog">
          <DialogHeader>
            <DialogTitle>Record Accessory Sale</DialogTitle>
            <DialogDescription>Add items to the cart and complete the sale.</DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-4">
            <div className="flex items-end gap-2">
              <div className="flex-1 space-y-2">
                <Label>Product</Label>
                <Select value={pick.accessory_id} onValueChange={(v) => setPick((p) => ({ ...p, accessory_id: v }))}>
                  <SelectTrigger data-testid="sale-product-select"><SelectValue placeholder="Select product" /></SelectTrigger>
                  <SelectContent>
                    {products.filter((p) => p.stock > 0).map((p) => (<SelectItem key={p.id} value={p.id}>{p.name} — {inr(p.price)} ({p.stock} in stock)</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-20 space-y-2"><Label>Qty</Label><Input type="number" min="1" value={pick.qty} onChange={(e) => setPick((p) => ({ ...p, qty: e.target.value }))} data-testid="sale-qty-input" /></div>
              <Button variant="outline" onClick={addToCart} data-testid="sale-add-item">Add</Button>
            </div>

            {cart.length > 0 && (
              <div className="border border-gray-100 rounded-lg divide-y" data-testid="sale-cart">
                {cart.map((i) => (
                  <div key={i.accessory_id} className="flex items-center justify-between px-3 py-2 text-sm">
                    <span>{i.name} × {i.qty}</span>
                    <span className="flex items-center gap-2">{inr(i.price * i.qty)}
                      <Button size="icon" variant="ghost" className="h-6 w-6 text-red-500" onClick={() => setCart((c) => c.filter((x) => x.accessory_id !== i.accessory_id))}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </span>
                  </div>
                ))}
                <div className="flex items-center justify-between px-3 py-2 font-bold"><span>Total</span><span data-testid="sale-total">{inr(cartTotal)}</span></div>
              </div>
            )}

            <div className="space-y-2"><Label>Customer Name (optional)</Label><Input value={customer} onChange={(e) => setCustomer(e.target.value)} placeholder="Walk-in customer" data-testid="sale-customer-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaleOpen(false)}>Cancel</Button>
            <Button onClick={completeSale} disabled={savingSale} className="bg-gradient-to-r from-amber-500 to-orange-500" data-testid="sale-complete-btn">
              {savingSale && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Complete Sale ({inr(cartTotal)})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccessoriesPage;
